import AnalyticsService from '@root/core/src/services/analytics-service';
import ClaimsPortalApi from '@root/claims.joinroot.com/src/api/claims-portal-api';
import React from '@root/vendor/react';
import RootLogo from '@root/core/src/components/root-logo';
import styles from '@root/claims.joinroot.com/src/components/total-loss-referral-consent/total-loss-referral-consent-styles';
import tlCar from '@root/claims.joinroot.com/src/components/total-loss-referral-consent/assets/tl_car.png';
import tlCarvanaLogo from '@root/claims.joinroot.com/src/components/total-loss-referral-consent/assets/tl_carvana_logo.png';
import tlGraphWithPaper from '@root/claims.joinroot.com/src/components/total-loss-referral-consent/assets/tl_graph_with_paper.png';
import { useEffect, useState } from '@root/vendor/react';
import { useLocation } from '@root/vendor/react-router-dom';

const REGISTERED_MARK = '®';
const CARVANA_NAME = `Carvana${REGISTERED_MARK}`;
const ROOT_CONSUMER_PRIVACY_NOTICE_URL = 'https://www.joinroot.com/consumer-privacy-notice/';
const ROOT_TERMS_OF_USE_URL = 'https://www.joinroot.com/terms-and-conditions/';
const CARVANA_CONSUMER_PRIVACY_NOTICE_URL = 'https://www.carvana.com/privacy-policy';

const CONSENT_HEADLINE = `Your $500 discount is almost ready. Next, you’ll continue to ${CARVANA_NAME}.`;
const CONSENT_BUTTON_TEXT = `Continue to ${CARVANA_NAME}`;

const CONSENT_TEXT_SEGMENTS = [
  { text: 'By clicking “' },
  { text: CONSENT_BUTTON_TEXT },
  {
    text:
      '” I authorize Root to share my personal information and data ' +
      'with ',
  },
  { text: CARVANA_NAME },
  {
    text:
      ', who may contact me about vehicle replacement options related to my claim. I agree to Root’s ',
  },
  {
    href: ROOT_CONSUMER_PRIVACY_NOTICE_URL,
    text: 'Consumer Privacy Notice',
  },
  { text: ', ' },
  {
    href: ROOT_TERMS_OF_USE_URL,
    text: 'Terms of Use',
  },
  { text: ', and Carvana’s ' },
  {
    href: CARVANA_CONSUMER_PRIVACY_NOTICE_URL,
    text: 'Consumer Privacy Notice',
  },
  { text: '.' },
];

const CONSENT_TEXT = CONSENT_TEXT_SEGMENTS.map(({ text }) => text).join('');

const HANDOFF_STEPS = [
  {
    image: tlCarvanaLogo,
    imageStyle: styles.appBadgeImage,
    title: `Set up your ${CARVANA_NAME} account`,
    description:
      `Create your account so ${CARVANA_NAME} can load your $500 discount and keep it ready while you shop.`,
  },
  {
    image: tlCar,
    imageStyle: styles.carImage,
    title: 'Find your next car',
    description:
      'See 360° car views and vehicle details. Filter results to fit your budget and explore financing options.',
  },
  {
    image: tlGraphWithPaper,
    imageStyle: styles.graphImage,
    title: 'Save $500 if you buy',
    description:
      'If you decide to buy, your discount applies automatically at checkout.',
  },
];

function renderTextWithRegisteredMarks(text, registeredMarkStyle) {
  const [firstTextPart, ...remainingTextParts] = text.split(REGISTERED_MARK);

  if (remainingTextParts.length === 0) { return text; }

  const renderedText = [firstTextPart];

  remainingTextParts.forEach((textPart) => {
    const keyPrefix = renderedText.filter((fragment) => typeof fragment === 'string').join('');

    renderedText.push(
      <sup
        css={registeredMarkStyle}
        key={`${keyPrefix}-registered-mark`}
      >
        &reg;
      </sup>
    );
    renderedText.push(textPart);
  });

  return renderedText;
}

export default function TotalLossReferralConsent() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('t');
  const totalLossReferralId = searchParams.get('referral_id');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const analyticsProperties = {
    total_loss_referral_id: totalLossReferralId,
    referral_partner_name: 'carvana',
    utm_source: searchParams.get('utm_source'),
    utm_content: searchParams.get('utm_content'),
  };

  useEffect(() => {
    if (!token) { return; }

    AnalyticsService.trackViewEvent('TOTAL_LOSS_REFERRAL_WEB_CONSENT_LANDING', {
      total_loss_referral_id: totalLossReferralId,
      referral_partner_name: 'carvana',
      utm_source: searchParams.get('utm_source'),
      utm_content: searchParams.get('utm_content'),
    });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!token) {
    // eslint-disable-next-line root/prevent-use-of-window-location
    window.location.assign('https://www.joinroot.com');
    return null;
  }

  const handleContinueToCarvana = () => {
    if (isSubmitting) { return; }

    setIsSubmitting(true);
    AnalyticsService.trackClickEvent('TOTAL_LOSS_REFERRAL_WEB', 'ACTIVATE_DISCOUNT', analyticsProperties);

    ClaimsPortalApi.postToPortal(
      '/web_api/claims/portal/total-loss-referral-consent/record-consent',
      {
        t: token,
        referral_id: totalLossReferralId,
        consent_terms: {
          headline: CONSENT_HEADLINE,
          consent_text: CONSENT_TEXT,
          benefits: HANDOFF_STEPS.map((step) => ({
            title: step.title,
            description: step.description,
          })),
        },
      },
      (response) => {
        const referralUrl = response.data.referral_url;

        setIsSubmitting(false);
        AnalyticsService.trackEvent('TOTAL_LOSS_REFERRAL_WEB', 'CONSENT_SUCCEEDED', analyticsProperties);

        if (!referralUrl) { return; }

        AnalyticsService.trackClickEvent('TOTAL_LOSS_REFERRAL_WEB', 'GO_TO_CARVANA', analyticsProperties);

        // Defer redirect by one tick so the Mixpanel call can flush before the page unloads.
        // eslint-disable-next-line root/prevent-use-of-window-location
        setTimeout(() => { window.location.assign(referralUrl); }, 0);
      },
      () => {
        setIsSubmitting(false);
        AnalyticsService.trackEvent('TOTAL_LOSS_REFERRAL_WEB', 'CONSENT_FAILED', analyticsProperties);
      }
    );
  };

  return (
    <div
      css={styles.page}
      data-testid={'total-loss-referral-consent'}
    >
      <span
        hidden
        data-testid={'total-loss-referral-consent-token'}
      >
        {token}
      </span>

      <div
        css={styles.header}
        role={'banner'}
      >
        <span css={styles.headerLogo}>
          <RootLogo />
        </span>
      </div>

      <main css={styles.landing}>
        <section css={styles.headlineColumn}>
          <h1 css={styles.headline}>
            {renderTextWithRegisteredMarks(CONSENT_HEADLINE, styles.headlineRegisteredMark)}
          </h1>
        </section>

        <section css={styles.actionColumn}>
          <div css={styles.steps}>
            {HANDOFF_STEPS.map((step, index) => (
              <div
                css={styles.step}
                key={step.title}
              >
                <div css={styles.stepText}>
                  <div css={styles.stepTitleRow}>
                    <span css={styles.stepNumber}>{index + 1}</span>
                    <h2 css={styles.stepTitle}>
                      {renderTextWithRegisteredMarks(step.title, styles.stepRegisteredMark)}
                    </h2>
                  </div>
                  <p css={styles.stepDescription}>
                    {renderTextWithRegisteredMarks(step.description, styles.stepRegisteredMark)}
                  </p>
                </div>
                <img
                  alt={''}
                  css={[styles.stepImage, step.imageStyle]}
                  src={step.image}
                />
              </div>
            ))}
          </div>

          <button
            aria-label={CONSENT_BUTTON_TEXT}
            css={styles.ctaButton}
            disabled={isSubmitting}
            onClick={handleContinueToCarvana}
            type={'button'}
          >
            {renderTextWithRegisteredMarks(CONSENT_BUTTON_TEXT, styles.buttonRegisteredMark)}
          </button>

          <p
            css={styles.consentText}
            data-testid={'total-loss-referral-consent-terms'}
          >
            {CONSENT_TEXT_SEGMENTS.map((segment) => {
              const content = renderTextWithRegisteredMarks(segment.text, styles.consentRegisteredMark);

              if (!segment.href) {
                return (
                  <React.Fragment key={segment.text}>
                    {content}
                  </React.Fragment>
                );
              }

              return (
                <a
                  css={styles.consentLink}
                  href={segment.href}
                  key={segment.href}
                  rel={'noopener noreferrer'}
                  target={'_blank'}
                >
                  {content}
                </a>
              );
            })}
          </p>
        </section>
      </main>
    </div>
  );
}
